import React, { useEffect, useState } from "react"
import { useSelector } from "react-redux"

import { MtUser } from "server/models"
import { useInput } from "client/utils"
import {
    StoreType,
    LoadUserByConfirmationCode,
    CheckPasswordRestrictions,
    ChangeUserPassword,
} from "client/store"

import { Footer } from "client/parts/layout"
import {
    Loader,
    FormGroup,
    Feedback,
    LostConnection,
} from "client/components"
import {
    AuthWrapper,
    AuthTitle,
    AuthFormWrapper,
    AuthForm,
    AuthInput,
    AuthButton,
    SuccessfulAuthWrapper,
    AuthFormDescription,
    LoginLinkWrapper,
    LoginLink,
} from "./styled"

interface ResetPasswordProps {
    confirmationCode: string
}

const ResetPassword = ({
    confirmationCode,
}: ResetPasswordProps) => {
    const [password, setPassword] = useInput("")
    const [repeatPassword, setRepeatPassword] = useInput("")

    const [user, setUser] = useState<MtUser>(null)
    const [passwordMismatch, setPasswordMismatch] = useState(false)
    const [passwordRestrictions, setPasswordRestrictions] = useState(true)

    const [isLoading, setIsLoading] = useState(true)
    const [isSaving, setIsSaving] = useState(false)
    const [passwordChanged, setPasswordChanged] = useState(false)

    const lang = useSelector((state: StoreType) => state.system.lang)
    const lostServerConnection = useSelector((state: StoreType) => state.system.lostServerConnection)

    useEffect(() => {
        LoadUserByConfirmationCode(confirmationCode)
        .then((res) => {
            setUser(res)
            setIsLoading(false)
        })
    }, [])

    const onConfirm = () => {
        const mismatch = password !== repeatPassword
        setPasswordMismatch(mismatch)

        const correctRestrictions = CheckPasswordRestrictions(password)
        setPasswordRestrictions(correctRestrictions)

        if (mismatch || !correctRestrictions) return

        setIsSaving(true)

        ChangeUserPassword(user.id, password)
        .then(() => {
            setPasswordChanged(true)
            setIsSaving(false)
        })
    }

    const allowSetPasswords = password.length && repeatPassword.length

    return (
        <AuthWrapper>
            { lostServerConnection && <LostConnection /> }
            {
                (isLoading || isSaving)
                && <Loader paddingTop={250} />
            }

            <AuthFormWrapper show={!isLoading && !user}>
                <AuthTitle>{lang.userNotFound}</AuthTitle>
                <AuthFormDescription>{lang.userNotFoundDescription}</AuthFormDescription>
            </AuthFormWrapper>

            <AuthFormWrapper show={!isLoading && user && !passwordChanged}>
                <AuthTitle>{lang.setNewPassword}</AuthTitle>
                <AuthForm>
                    <FormGroup>
                        <AuthInput
                            type="password"
                            value={password}
                            onChange={setPassword}
                            placeholder={lang.password}
                        />
                    </FormGroup>
                    <FormGroup>
                        <AuthInput
                            type="password"
                            value={repeatPassword}
                            onChange={setRepeatPassword}
                            placeholder={lang.repeat}
                        />
                        <Feedback>{passwordMismatch && lang.passwordMismatch}</Feedback>
                        <Feedback>{!passwordRestrictions && lang.passwordRestrictions}</Feedback>
                    </FormGroup>

                    <AuthButton
                        variant="success"
                        disabled={!allowSetPasswords}
                        onClick={onConfirm}
                    >
                        {lang.setPassword}
                    </AuthButton>
                </AuthForm>
            </AuthFormWrapper>

            <SuccessfulAuthWrapper show={passwordChanged}>
                <AuthTitle>{lang.passwordChanged}</AuthTitle>
                <LoginLinkWrapper>
                    <LoginLink href="/login">{lang.goToLogin}</LoginLink>
                </LoginLinkWrapper>
            </SuccessfulAuthWrapper>

            <Footer />
        </AuthWrapper>
    )
}
export default ResetPassword
